/**
 * The dictation's waveform (board „Buddy P5 · Stany", the owner's dictation): one continuous run of red
 * bars filling the room between „Słucham…" and the timer, one bar per 80 ms of voice, newest by the timer, the
 * history scrolling left and fading out before the word. Drawn on a canvas in `useAnimationFrame`, so no
 * frame is React state; the level is read from the recorder when the frame wants it.
 *
 *   · flat      waiting for the browser's permission: a few dim dots in the strip, breathing
 *   · live      recording: an envelope follower (fast attack, slow release) over the level, a bar
 *               for the loudest moment of each window, the newest one growing in
 *   · collapse  transcribing: the bars fall to dots, newest first, and a highlight runs across them
 *
 * Reduced motion draws a still shape: no scroll, no growth, no shimmer. The ink is `--danger`, read
 * off the computed style so a theme change reaches the canvas: a fill, never a glow.
 */

import { useAnimationFrame } from 'motion/react';
import { useEffect, useRef } from 'react';

import type { WaveMode } from '../model/dictation';
import styles from './Composer.module.css';

const WINDOW_MS = 80;
const BAR = 3;
const PITCH = 6;
const LOW = 3;
const HIGH = 18;
const GROW_MS = 120;
const FALL_MS = 260;
const FALL_STAGGER_MS = 4;
const FALL_STAGGER_CAP_MS = 300;
const SHINE_AFTER_MS = 420;
const SHINE_EVERY_MS = 1300;
const SHINE_SIGMA = 28;
const ATTACK = 0.5;
const RELEASE = 0.12;
const DOT_BELOW = 0.05;
const INK_EVERY_MS = 500;
const EDGE_LOW = 0.25;
const EDGE_HIGH = 0.8;
const FADE_PX = 72;

interface Bar {
  readonly level: number;
  readonly born: number;
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;

/** A fixed, speech-like shape for reduced motion: the same every time, moving never. */
const stillLevel = (index: number) => {
  const phrase = 0.5 + 0.5 * Math.sin(index * 0.072) * Math.sin(index * 0.03 + 1.3);
  const syllable = Math.abs(Math.sin(index * 1.17)) ** 1.4;
  return phrase > 0.22 ? Math.min(1, (phrase - 0.22) * 3.2) * (0.28 + 0.62 * syllable) : 0;
};

function pill(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) {
  const radius = Math.min(width, height) / 2;
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fill();
}

export interface DictationWaveProps {
  readonly mode: WaveMode;
  readonly readLevel: () => number;
  readonly reducedMotion: boolean;
}

export function DictationWave({ mode, readLevel, reducedMotion }: DictationWaveProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const bars = useRef<Bar[]>([]);
  const envelope = useRef({ level: 0, peak: 0, lastPush: 0, modeAt: 0, ink: '', inkAt: -Infinity });
  const size = useRef({ width: 0, height: 0 });

  // A new dictation starts from an empty history; transcribing keeps the one it falls from.
  useEffect(() => {
    const now = performance.now();
    envelope.current.modeAt = now;
    if (mode !== 'collapse') {
      bars.current = [];
      envelope.current.lastPush = now;
      envelope.current.level = 0;
    }
  }, [mode]);

  useEffect(() => {
    const node = canvas.current;
    if (node === null) return;
    const fit = () => {
      const box = node.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      size.current = { width: box.width, height: box.height };
      node.width = Math.max(1, Math.round(box.width * ratio));
      node.height = Math.max(1, Math.round(box.height * ratio));
      node.getContext('2d')?.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);

  // Motion hands the callback the time since the hook mounted; every stamp here is
  // `performance.now()`, so the frame reads that clock too.
  useAnimationFrame(() => {
    const now = performance.now();
    const node = canvas.current;
    const context = node?.getContext('2d');
    if (node === null || context === null || context === undefined) return;
    const state = envelope.current;
    const { width, height } = size.current;
    if (now - state.inkAt > INK_EVERY_MS) {
      state.ink = getComputedStyle(node).getPropertyValue('--danger').trim();
      state.inkAt = now;
    }
    context.clearRect(0, 0, width, height);
    context.fillStyle = state.ink;
    const middle = height / 2;
    const room = Math.ceil(width / PITCH) + 2;
    // One continuous run: full strength from the timer back, fading out over its oldest stretch so
    // it never runs into „Słucham…" and the microphone.
    const edge = (x: number) => EDGE_LOW + (EDGE_HIGH - EDGE_LOW) * Math.min(1, Math.max(0, (x + BAR / 2) / FADE_PX));

    if (mode === 'flat') {
      const breath = reducedMotion ? 0.6 : 0.45 + 0.3 * (0.5 + 0.5 * Math.sin(now / 520));
      for (let x = width - BAR; x > -BAR; x -= PITCH) {
        context.globalAlpha = breath * edge(x);
        pill(context, x, middle - LOW / 2, BAR, LOW);
      }
      context.globalAlpha = 1;
      return;
    }

    if (reducedMotion) {
      for (let k = 0, x = width - BAR; x > -BAR; k += 1, x -= PITCH) {
        const level = stillLevel(k);
        const tall = mode === 'collapse' ? LOW : LOW + level * (HIGH - LOW);
        context.globalAlpha = (mode === 'collapse' ? 0.45 : level < DOT_BELOW ? 0.42 : 1) * edge(x);
        pill(context, x, middle - tall / 2, BAR, tall);
      }
      context.globalAlpha = 1;
      return;
    }

    if (mode === 'live') {
      const raw = readLevel();
      state.level += (raw - state.level) * (raw > state.level ? ATTACK : RELEASE);
      state.peak = Math.max(state.peak, state.level);
      if (now - state.lastPush >= WINDOW_MS) {
        bars.current.push({ level: state.peak, born: now });
        if (bars.current.length > room) bars.current.splice(0, bars.current.length - room);
        state.peak = 0;
        state.lastPush = now;
      }
    }

    const history = bars.current;
    const drift = mode === 'live' ? Math.min(1, (now - state.lastPush) / WINDOW_MS) : 0;
    const since = now - state.modeAt;
    for (let k = 0; k < history.length; k += 1) {
      const bar = history[history.length - 1 - k];
      const x = width - BAR - (k + drift) * PITCH;
      if (bar === undefined || x < -BAR) break;
      let tall = LOW + (LOW + bar.level * (HIGH - LOW) - LOW) * easeOut(Math.min(1, (now - bar.born) / GROW_MS));
      let alpha = bar.level < DOT_BELOW ? 0.42 : 1;
      if (mode === 'collapse') {
        const fall = easeOut(Math.max(0, Math.min(1, (since - Math.min(k * FALL_STAGGER_MS, FALL_STAGGER_CAP_MS)) / FALL_MS)));
        tall += (LOW - tall) * fall;
        const shining = since - SHINE_AFTER_MS;
        if (shining > 0) {
          const head = ((shining % SHINE_EVERY_MS) / SHINE_EVERY_MS) * (width + 4 * SHINE_SIGMA) - 2 * SHINE_SIGMA;
          alpha = 0.3 + 0.7 * Math.exp(-(((x - head) / SHINE_SIGMA) ** 2));
        } else {
          alpha = 1 - 0.5 * fall;
        }
      }
      context.globalAlpha = alpha * edge(x);
      pill(context, x, middle - tall / 2, BAR, tall);
    }
    context.globalAlpha = 1;
  });

  return (
    <span className={styles.waveBox}>
      <canvas ref={canvas} className={styles.wave} aria-hidden="true" />
    </span>
  );
}
